import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import Select from 'react-select';
import CountryCodePicker from '../../components/CountryCodePicker';
import { formatInTimeZone } from 'date-fns-tz';
import TimezoneSwitcher from '../../components/TimezoneSwitcher';
import { timezones } from '../../data/timezones';
import './Support.css';
const API_URL = process.env.REACT_APP_API_URL;
const CreateQuotePage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  
  const [formData, setFormData] = useState({
    customerId: null,
    customerEmail: '',
    customerPhoneCountryCode: '+90',
    customerPhoneNumber: '',
    sourceLang: 'tr',
    targetLangs: [],
    expertise: 'general',
    wordCount: 0,
    price: '',
    currency: 'TRY',
    translatorType: 'professional_translator',
    deliverySpeed: 'standard',
    requiresDtp: false,
    requiresShipping: false,
    requiresNotary: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dbLanguages, setDbLanguages] = useState([]);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [quoteLink, setQuoteLink] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCustomerNew, setIsCustomerNew] = useState(true);
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);
  const [isCounting, setIsCounting] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
        if(!token) return;
        try {
            const [customersRes, languagesRes, configRes] = await Promise.all([
                fetch(`${API_URL}/api/support/users?role=customer`, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(`${API_URL}/api/public/languages`),
                fetch(`${API_URL}/api/public/pricing-config`)
            ]);
            const customersData = await customersRes.json();
            const languagesData = await languagesRes.json();
            const configData = await configRes.json();
            const customerOptions = customersData.map(c => ({ value: c.id, label: `${c.first_name} ${c.last_name} (${c.email})`, ...c }));
            setCustomers(customerOptions);
            setDbLanguages(languagesData);
            setPricingConfig(configData);
        } catch(err) {
            console.error(err);
            setError(t('error_fetch_initial_data'));
        }
    };
    fetchInitialData();
  }, [token, t]);
  
  useEffect(() => {
    if (!pricingConfig || formData.wordCount <= 0 || formData.targetLangs.length === 0) {
      setFormData(p => ({ ...p, price: '' }));
      return;
    }
    
    let priceInTry = 0;
    const { wordCount, targetLangs, translatorType, expertise, deliverySpeed, requiresDtp, requiresShipping, requiresNotary } = formData;
    const { base_fee_amount, base_fee_word_limit } = pricingConfig;
    
    // 1. Yeni Taban Ücret Mantığı
    priceInTry += base_fee_amount || 0;
    if (wordCount > base_fee_word_limit) {
        const extraWords = wordCount - base_fee_word_limit;
        let extraWordsPrice = 0;
        targetLangs.forEach(lang => {
            const rateKey = `rate_per_word_${lang}`;
            const rate = pricingConfig[rateKey] || pricingConfig.rate_per_word_other;
            extraWordsPrice += extraWords * rate;
        });
        priceInTry += extraWordsPrice;
    }
    
    // 2. Çarpanları uygula
    if (translatorType === 'sworn_translator') priceInTry *= pricingConfig.multiplier_sworn;
    if (pricingConfig[`multiplier_${expertise}`]) priceInTry *= pricingConfig[`multiplier_${expertise}`];
    
    // 3. Hızlı teslimat çarpanını, SADECE çeviri maliyetine değil, o ana kadarki toplama uygula
    if (deliverySpeed === 'fast') {
        priceInTry *= pricingConfig.fast_delivery_multiplier;
    }
    
    // 4. Sabit ek hizmet ücretlerini en son ekle
    if (requiresDtp) priceInTry += pricingConfig.fee_dtp;
    if (requiresShipping) priceInTry += pricingConfig.fee_shipping;
    if (requiresNotary) priceInTry += pricingConfig.fee_notary;

    setFormData(p => ({ ...p, price: priceInTry.toFixed(2) }));
  }, [
    formData.wordCount, formData.targetLangs, formData.expertise, formData.translatorType, 
    formData.deliverySpeed, formData.requiresDtp, formData.requiresShipping, formData.requiresNotary,
    pricingConfig
  ]);
  
  const estimatedDeliveryDate = useMemo(() => {
    if (!formData.wordCount || formData.wordCount <= 0 || formData.targetLangs.length === 0) return null;
    let deliveryDays = 0, deliveryHours = 0; const wc = formData.wordCount;
    if (wc < 2000) { const baseHours = (wc / 400) * 9; deliveryHours = (formData.deliverySpeed === 'fast') ? baseHours / 2 : baseHours; } 
    else { if (wc <= 4000) { deliveryDays = 2; } else if (wc <= 6000) { deliveryDays = 3; } else if (wc <= 10000) { deliveryDays = 4; } else if (wc <= 15000) { deliveryDays = 6; } else if (wc <= 20000) { deliveryDays = 8; } else if (wc <= 25000) { deliveryDays = 12; } else if (wc <= 35000) { deliveryDays = 16; } else if (wc <= 45000) { deliveryDays = 20; } else if (wc <= 55000) { deliveryDays = 24; } else { const baseDays = 24; const additionalWords = wc - 55000; const additionalBlocks = Math.ceil(additionalWords / 10000); deliveryDays = baseDays + (additionalBlocks * 4); } if (formData.deliverySpeed === 'fast') { deliveryDays = deliveryDays / 2; } }
    const deliveryDate = new Date();
    if (deliveryDays > 0) { deliveryDate.setDate(deliveryDate.getDate() + Math.max(1, Math.ceil(deliveryDays))); } else { deliveryDate.setHours(deliveryDate.getHours() + Math.max(1, Math.ceil(deliveryHours))); }
    return deliveryDate;
  }, [formData.wordCount, formData.targetLangs, formData.deliverySpeed]);

  const languageOptions = useMemo(() => dbLanguages.map(lang => ({ value: lang.code, label: t(`lang_${lang.code}`) })), [dbLanguages, i18n.language, t]);
  const handleCustomerSelect = (selectedOption) => {
    setIsCustomerNew(!selectedOption);
    setFormData(prev => ({ ...prev, customerId: selectedOption ? selectedOption.value : null, customerEmail: selectedOption ? selectedOption.email : '', customerPhoneCountryCode: selectedOption ? selectedOption.phone_country_code || '+90' : '+90', customerPhoneNumber: selectedOption ? selectedOption.phone_number || '' : '' }));
  };
  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleCountryCodeChange = (code) => setFormData(prev => ({ ...prev, customerPhoneCountryCode: code }));
  const handleTargetLangsChange = (opts) => setFormData(prev => ({ ...prev, targetLangs: opts ? opts.map(o => o.value) : [] }));
  const handleSourceLangChange = (opt) => setFormData(p => ({ ...p, sourceLang: opt.value, targetLangs: p.targetLangs.filter(lang => lang !== opt.value) }));
  const handleTimezoneChange = (tz) => setFormData(prev => ({ ...prev, timezone: tz }));
  
   const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
        // DÜZELTME: submissionData artık 'uploadedFiles' dizisini de içeriyor
        const submissionData = {
            ...formData,
            // 'file' objesini kaldırdık, sadece gerekli bilgileri gönderiyoruz
            uploadedFiles: uploadedFiles.map(f => ({
                originalFilename: f.file.name,
                storedFilename: f.storedFilename, // Bu bilgiyi word-count'tan almamız lazım
                wordCount: f.wordCount
            })),
            deadline: estimatedDeliveryDate ? estimatedDeliveryDate.toISOString() : null};
        const response = await fetch(`${API_URL}/api/quotes/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(submissionData)
        });
		
		const data = await response.json();
        if (!response.ok) throw new Error(t(data.messageKey) || 'Teklif oluşturulamadı.');
        setQuoteLink(data.quoteLink);
    } catch (err) { 
        setError(err.message);
    } finally {
        setLoading(false);
	   }
  };

        
        
  
  // DÜZELTME: handleFileChange artık 'storedFilename' bilgisini de saklıyor
  const handleFileChange = async (e) => {
    const filesToUpload = Array.from(e.target.files);
    if (filesToUpload.length === 0) return;
    setIsCounting(true);
    let totalAddedWords = 0;
    let addedFiles = [];
    for (const file of filesToUpload) {
      const formData = new FormData();
      formData.append('document', file);
      try {
        const response = await fetch(`${API_URL}/api/word-count`, { method: 'POST', body: formData });
        if (!response.ok) throw new Error(`Dosya işlenemedi: ${file.name}`);
        const result = await response.json();
        addedFiles.push({
            file: file,
            wordCount: result.wordCount,
            storedFilename: result.storedFilename // Bu bilgi word-count'tan gelmeli
        });
        totalAddedWords += result.wordCount;
      } catch (error) { alert(error.message); }
    }
    setUploadedFiles(prev => [...prev, ...addedFiles]);
    setFormData(p => ({ ...p, wordCount: (Number(p.wordCount) || 0) + totalAddedWords }));
    setIsCounting(false);
  };

  const removeFile = (fileIndexToRemove) => {
    const fileToRemove = uploadedFiles[fileIndexToRemove];
    if (!fileToRemove) return;
    setFormData(p => ({ ...p, wordCount: Math.max(0, p.wordCount - fileToRemove.wordCount) }));
    setUploadedFiles(prev => prev.filter((_, index) => index !== fileIndexToRemove));
  };

 

  const expertiseOptions = ['general', 'legal', 'medical', 'technical', 'academic'].map(e => ({ value: e, label: t(e) }));
  const selectedTargetLangs = languageOptions.filter(opt => formData.targetLangs.includes(opt.value));
  const selectedSourceLang = languageOptions.find(opt => opt.value === formData.sourceLang);

  return (
    <>
      <div className="page-container admin-dashboard-narrow-content">
        <h1>{t('create_new_quote')}</h1>
        <div className="account-content-card">
          <form onSubmit={handleSubmit} className="modern-form create-quote-form">
            <div className="form-section">
             <h4><i className="fas fa-user-circle"></i>{t('customer_info')}</h4>
              <div className="form-group-auth"><label>{t('select_existing_customer_optional')}</label><Select options={customers} onChange={handleCustomerSelect} isClearable placeholder={t('search_customer_placeholder')} /></div>
              <div className="form-group-auth floating-label"><input id="customerEmail" name="customerEmail" type="email" value={formData.customerEmail} onChange={handleChange} required placeholder=" " disabled={!isCustomerNew} /><label htmlFor="customerEmail">{t('email_address')}</label></div>
              <div className="form-row-auth">
                <div className="form-group-auth phone-code"><CountryCodePicker selectedCode={formData.customerPhoneCountryCode} onCodeChange={handleCountryCodeChange} /></div>
                <div className="form-group-auth floating-label phone-number"><input id="customerPhoneNumber" name="customerPhoneNumber" type="tel" value={formData.customerPhoneNumber} onChange={handleChange} placeholder=" " /><label htmlFor="customerPhoneNumber">{t('phone')}</label></div>
              </div>
            </div>
            <div className="form-section">
               <h4><i className="fas fa-upload"></i>{t('upload_documents')}</h4>
                <div className="file-upload-content quote-upload">
                    <label htmlFor="quote-file-upload" className={`upload-btn-styled ${isCounting ? 'disabled' : ''}`}>
                        {isCounting ? <><i className="fas fa-spinner fa-spin"></i> {t('counting_words')}</> : <><i className="fas fa-file-import"></i> {t('select_documents')}</>}
                    </label>
                    <input id="quote-file-upload" type="file" onChange={handleFileChange} multiple disabled={isCounting} style={{display: 'none'}} />
                    {uploadedFiles.length > 0 && (
                        <div className="file-list-container">
                            {uploadedFiles.map((fileData, index) => (
                                <div key={index} className="file-list-item">
                                    <span><i className="fas fa-file-alt"></i> {fileData.file.name}</span>
                                    <span className="file-word-count">{fileData.wordCount} {t('words')}</span>
                                    <button type="button" onClick={() => removeFile(index)} className="remove-file-btn"><i className="fas fa-times"></i></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            
            <div className="form-section">
              <h4><i className="fas fa-user-tie"></i> {t('step3_title_quote')}</h4>
              <div className="choice-buttons"><button type="button" onClick={() => setFormData(p => ({...p, translatorType: 'professional_translator'}))} className={formData.translatorType === 'professional_translator' ? 'active' : ''}>{t('professional_translator')}</button><button type="button" onClick={() => setFormData(p => ({...p, translatorType: 'sworn_translator'}))} className={formData.translatorType === 'sworn_translator' ? 'active' : ''}>{t('sworn_translator')}</button></div>
            </div>
          <div className="form-section">
              <h4><i className="fas fa-bolt"></i>{t('step4_title_quote', 'Teslimat Hızı')}</h4>
              <div className="choice-buttons">
                <button type="button" onClick={() => setFormData(p => ({...p, deliverySpeed: 'standard'}))} className={formData.deliverySpeed === 'standard' ? 'active' : ''}>{t('standard_delivery')}</button>
                <button type="button" onClick={() => setFormData(p => ({...p, deliverySpeed: 'fast'}))} className={formData.deliverySpeed === 'fast' ? 'active' : ''}>{t('fast_delivery')}</button>
              </div>
              
            
            </div>
            <div className="form-section">
             <h4><i className="fas fa-plus-circle"></i> {t('step5_title_quote')}</h4>
              <div className="checkbox-group"><label className="auth-checkbox"><input type="checkbox" name="requiresDtp" checked={formData.requiresDtp} onChange={(e) => setFormData(p=>({...p, requiresDtp: e.target.checked}))} /><span>{t('dtp_service')}</span></label><label className="auth-checkbox"><input type="checkbox" name="requiresShipping" checked={formData.requiresShipping} onChange={(e) => setFormData(p=>({...p, requiresShipping: e.target.checked}))} /><span>{t('shipping_service')}</span></label><label className="auth-checkbox"><input type="checkbox" name="requiresNotary" checked={formData.requiresNotary} onChange={(e) => setFormData(p=>({...p, requiresNotary: e.target.checked}))} /><span>{t('notary_service')}</span></label></div>
            </div>
			<div className="form-section">
             <h4><i className="fas fa-list-alt"></i>{t('quote_details')}</h4>
              <div className="form-row-auth">
                <div className="form-group-auth half-width"><label>{t('source_language')}</label><Select value={selectedSourceLang} options={languageOptions} onChange={handleSourceLangChange} /></div>
                <div className="form-group-auth half-width"><label>{t('target_language')}</label><Select isMulti value={selectedTargetLangs} options={languageOptions} onChange={handleTargetLangsChange} /></div>
              </div>
              <div className="form-group-auth"><label>{t('expertise_area')}</label><Select options={expertiseOptions} defaultValue={expertiseOptions.find(e => e.value === 'general')} onChange={opt => setFormData(p => ({...p, expertise: opt.value}))} /></div>
              <div className="form-row-auth">
                <div className="form-group-auth floating-label half-width"><input type="number" id="wordCount" name="wordCount" value={formData.wordCount} onChange={handleChange} required placeholder=" " /><label htmlFor="wordCount">{t('word_count')}</label></div>
                <div className="form-group-auth floating-label half-width"><input type="number" step="0.01" id="price" name="price" value={formData.price} onChange={handleChange} required placeholder=" " /><label htmlFor="price">{`${t('price')} (${formData.currency})`}</label></div>
              </div>
			    {/* Bu blok tamamen eksikti */}
              {estimatedDeliveryDate && (
                <div className="delivery-info-container">
                  <p className="estimated-delivery-info">
                    {t('estimated_delivery')}:  
                    <strong> {formatInTimeZone(estimatedDeliveryDate, formData.timezone, 'dd.MM.yyyy HH:mm')}</strong>
                  </p>
                  <div className="timezone-display-input-inline" onClick={() => setIsTimezoneOpen(true)}>
                    <span>{t('local_timezone')}:</span>
                    <strong>{formData.timezone}</strong>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                </div>
              )}
            </div>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" className="cta-button" disabled={loading}>{loading ? t('creating_quote') : t('create_quote_btn')}</button>
          </form>
          {quoteLink && (
              <div className="quote-link-box">
                  <p>{t('quote_link_generated')}</p>
                  <input type="text" value={quoteLink} readOnly onFocus={(e) => e.target.select()} />
                  <button type="button" onClick={() => navigator.clipboard.writeText(quoteLink)}>{t('copy_link')}</button>
              </div>
          )}
        </div>
      </div>
      <TimezoneSwitcher isOpen={isTimezoneOpen} onClose={() => setIsTimezoneOpen(false)} timezones={timezones} selectedTimezone={formData.timezone} onTimezoneChange={handleTimezoneChange} />
    </>
  );
};

export default CreateQuotePage;